import React from "react";
import CustomGenericTittleIconDataChip from "../../../../components/CustomGenericTittleIconDataChip";
import {
    ArrowRightIcon,
    IconBuildingDocument
} from "../../../../components/buttons/IconButtons";
import {useTranslation} from "react-i18next";
import {ButtonLargeTransparent} from "../../../../components/buttons/ButtonLargeTransparent";
import {getUrlParameters} from "../../../Dashboard/Visitor/External/Model/Model";
import routes from "../../../../Routes/routes";
import {errorMsg, isEmailFormat} from "../../../../Apis/functions";
import {toast} from "react-toastify";
import {updateVeridasUser} from "../../../../reduxToolkit/Visitor/VisitorApi";
import {PublicProcessCompleted} from "../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi";
import {getFailedUrl, removeStoredBuildingApartmentId, setStoredBuildingApartmentId} from "../../../Veridas/Model/Models";
import {setBuildingRegistrationResident} from "../../../../reduxToolkit/Building/BuildingSlice";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
import {BUILDING_DETAIL_MODEL} from "../../Model/Model";

const ProceedWithRegistrationHeader = () => {
    return (
        <CustomGenericTittleIconDataChip Icon={IconBuildingDocument} title={'building_registration'} border={false} className={" align-items-start"}/>
    );
};

const ProceedWithRegistrationBody = ({ buildingId, userId, onStartRegistration, loading }) => {
    const { t } = useTranslation();

    // The link copied for one resident already carries the user the owner
    // registered for them, their identity is read with it and the resident is
    // never asked to identify themselves again
    const url = userId
        ? null
        : getUrlParameters({url: buildingRegistrationUrl(routes.buildingRegistration.identifyYou, buildingId)});

    return (
        <>
            <div className='my-3'>
                <p className={'font-color-general-950 fs-14px ibl-font-w-600'}>{t('greeting')}</p>
                <p className={'font-color-general-950 fs-14px ibl-font-w-400'}>{t('you_have_been_invited_to_register_as_a_resident')}</p>
            </div>

            <div>
                <ButtonLargeTransparent
                    text={t('start_registration')}
                    className='btn-shades-700'
                    icon={<ArrowRightIcon width={24} height={24}/>}
                    iconColor='icon-color-general-00'
                    disabled={(!buildingId && !userId) || loading}
                    click={userId ? onStartRegistration : null}
                    url={url}
                />
            </div>
        </>
    );
};

/**
 * Builds one url of the external building registration. The building is always
 * the first parameter and the user is only added when the link was copied for
 * one of them.
 *
 * @param {string} url - Route of the step, it ends with a slash.
 * @param {string} buildingId - Building the registration belongs to.
 * @param {string} [userId] - User the link was copied for.
 * @returns {string} Url of the step with its parameters.
 */
export const buildingRegistrationUrl = (url, buildingId, userId) => {
    return userId ? `${url}${buildingId}/${userId}` : `${url}${buildingId}`;
};

export const BUILDING_REGISTRATION_STATUS_MODEL_CARD = {
    PROCEED_WITH_REGISTRATION: {//Guard flow
        ID: null,
        NAME: '',
        DIVIDER: true,
        HEADER: (props) => <ProceedWithRegistrationHeader />,
        BODY: ({ buildingId, userId, onStartRegistration, loading }) => (
            <ProceedWithRegistrationBody
                buildingId={buildingId}
                userId={userId}
                onStartRegistration={onStartRegistration}
                loading={loading}
            />
        ),
    },
};

/**
 * Steps of the external building registration flow. The identify you form is
 * the only step the resident fills before the identity validation takes over.
 */
export const BUILDING_REGISTRATION_STEP_MODEL = {
    IDENTIFY_YOU: {
        TOTAL: 1,
        NAME: 'IDENTIFY_YOU'
    }
};

/**
 * Form model of the identify you step, it holds the data the invited resident
 * captures before validating their identity. The email is optional and it is
 * only validated by format.
 */
export const BUILDING_REGISTRATION_IDENTITY_MODEL = {
    name: '',
    lastName: '',
    email: '',
    unit: '',
    isValid: function () {
        return this.name !== null && this.name !== ''
            && this.lastName !== null && this.lastName !== ''
            && this.unit !== null && this.unit !== ''
            && this.email !== null && this.email !== '' && isEmailFormat(this.email);
    },
    /**
     * Payload of building-service/v1/resident/validate-owner, the unit captured
     * by the resident travels as the apartment the service looks for.
     *
     * @param {string} buildingId - Building the resident is registering in.
     * @returns {Object} Body of the validation.
     */
    toValidateOwner: function (buildingId) {
        return {
            buildingId,
            name: this.name,
            lastName: this.lastName,
            email: this.email,
            apartment: this.unit
        }
    }
};

/**
 * Building of the registration read from the apartment of the link. The flow
 * that starts on the link of one resident never carries the building, the
 * apartment travels in its place and building-apartment/details answers the
 * name of the building and the coordinates the success screen paints.
 *
 * @param {Object} data - Apartment returned by building-apartment/details.
 * @returns {Object} Detail of the building ready to be rendered.
 */
export const toBuildingRegistrationDetail = (data) => ({
    ...BUILDING_DETAIL_MODEL,
    name: data?.buildingName ?? '',
    statusId: data?.statusId ?? '',
    zoneId: data?.zoneId ?? '',
    latitude: data?.latitude ?? '',
    longitude: data?.longitude ?? ''
});

/**
 * Resolves the answer of user-service/public/verify-identity and moves the
 * resident to the step that follows it. The service answers whether the
 * identity of the user is still pending: true sends them into the validation
 * and false means the registration was already finished before.
 *
 * The link copied for one resident does not carry the building, their apartment
 * travels in its place through every step of the flow.
 *
 * @param {Object} params
 * @param {Object} params.payload - Payload the PublicVerifyIdentity thunk answered with.
 * @param {string} params.buildingApartmentId - Apartment the link was copied for.
 * @param {string} params.userId - User the link was copied for.
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {Function} params.navigate - Router navigation function.
 * @returns {boolean} Whether the answer moved the flow forward.
 */
export const resolveVerifiedIdentity = ({payload, buildingApartmentId, userId, dispatch, navigate}) => {
    const {success, data, code} = payload?.data ?? {};

    if (!success) {
        errorMsg(code);
        return false;
    }

    // Every step that follows takes the apartment of the link in the place of
    // the building, keeping it is what tells the flow started from the link of
    // one resident and that there is no identify you form behind it. It is also
    // stored because a reload of any step empties the redux one
    dispatch(setBuildingRegistrationResident({userId, buildingApartmentId}));
    setStoredBuildingApartmentId(buildingApartmentId);

    // The identity was validated before, the flow only takes the resident to
    // the screen that confirms the registration is already done
    if (data !== true) {
        navigate(getUrlParameters({
            url: buildingRegistrationUrl(routes.buildingRegistration.successfullyVerified, buildingApartmentId, userId)
        }));
        return true;
    }

    // The validation steps only take the user and the building, the apartment
    // of the link travels in the place of the building on this flow
    navigate(getUrlParameters({
        url: `${routes.buildingRegistration.chooseValidationType}${userId}/${buildingApartmentId}`
    }));

    return true;
};

/**
 * Resolves the answer of the validation that starts the registration and moves
 * the resident to the step that follows it. It is shared by the two ways of
 * starting it: the identify you form, validated by
 * building-service/v1/resident/validate-owner, and the link that already carries
 * the user, read by user-service/public/verify-identity.
 *
 * @param {Object} params
 * @param {Object} params.payload - Payload the validation thunk answered with.
 * @param {string} [params.buildingId] - Building the resident is registering in, the
 *   link copied for one resident does not carry it and the service resolves it.
 * @param {string} [params.buildingApartmentId] - Apartment the link was copied for, when it carries one.
 * @param {Object} [params.resident] - Data captured by the resident, when there is a form behind.
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {Function} params.navigate - Router navigation function.
 * @param {Function} params.t - Translation function.
 * @returns {boolean} Whether the validation moved the flow forward.
 */
export const resolveValidatedOwner = ({payload, buildingId, buildingApartmentId, resident, dispatch, navigate, t}) => {
    const {success, data} = payload?.data ?? {};
    const validatedUserId = data?.userId ?? data?.id;
    // The link copied for one resident travels with their apartment, the building
    // of the registration is the one the validation of the owner answers with
    const resolvedBuildingId = buildingId ?? data?.buildingId;

    // Without the user of the owner there is nothing to validate against,
    // the resident did not capture the data the owner registered for them
    if (!success || !validatedUserId || !resolvedBuildingId) {
        toast.error(t("the_data_entered_does_not_match_the_registered_information"));
        return false;
    }

    // This registration goes through the identify you form and travels with a
    // real building, any apartment stored by a previous link is dropped
    removeStoredBuildingApartmentId();

    // The resident already validated their identity before, the flow only
    // takes them to the screen that confirms the registration is done
    if (data?.identityValidationId === STATUS_MODEL.VALIDATED) {
        navigate(getUrlParameters({
            url: buildingRegistrationUrl(routes.buildingRegistration.successfullyVerified, resolvedBuildingId, validatedUserId)
        }));
        return true;
    }

    // The summary of the success screen is painted with the data the resident
    // captured, or with the one the service answers with when there was no form,
    // there is no endpoint that returns the registration back. The apartment of
    // the link travels with them, it is the one the registration is closed with
    // and the service resolves it when the link did not carry it
    dispatch(setBuildingRegistrationResident({
        userId: validatedUserId,
        buildingApartmentId: buildingApartmentId ?? data?.buildingApartmentId,
        unit: resident?.unit ?? data?.apartment,
        email: resident?.email ?? data?.email
    }));

    // From here on the user of the registration is the one the service
    // answered with, it is the only parameter the validation steps need
    navigate(getUrlParameters({
        url: `${routes.buildingRegistration.chooseValidationType}${validatedUserId}/${resolvedBuildingId}`
    }));

    return true;
};

/**
 * Handles a successful building registration: stops the loader, tells the
 * resident the registration is done and moves to the success summary.
 *
 * @param {Object} params
 * @param {Function} params.setLoading - Loading state setter.
 * @param {Function} params.t - Translation function.
 * @param {Function} params.navigate - Router navigation function.
 * @param {string} params.successUrl - Success redirect URL.
 */
const handleSuccess = ({ setLoading, t, navigate, successUrl }) => {
    setLoading(false);
    toast.success(t('has_been_registered_successfully'));
    navigate(successUrl);
};

/**
 * Handles a failed building registration and redirects to the failure step of
 * the building registration flow.
 *
 * @param {Object} params
 * @param {Function} params.setLoading - Loading state setter.
 * @param {Function} params.navigate - Router navigation function.
 * @param {string|number} params.resolvedUserId - User identifier.
 * @param {string|number} params.buildingId - Building identifier.
 */
const handleFailed = ({ setLoading, navigate, resolvedUserId, buildingId }) => {
    setLoading(false);
    navigate(getFailedUrl({ userId: resolvedUserId, buildingId }));
};

/**
 * Closes the registration of one resident in a building. Unlike the event flow
 * there is no invitation to create nor a visitor status to update, the resident
 * was already matched against the building in the identify you step, so the
 * only thing left is marking the process as completed.
 *
 * TODO: call the building registration endpoint here once it is available, the
 * resident has to be linked to buildingApartmentId with the validated identity.
 *
 * @async
 * @param {Object} params
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {string|number} params.resolvedUserId - User identifier.
 * @param {string|number} params.buildingId - Building identifier.
 * @param {string} [params.buildingApartmentId] - Apartment the resident is linked to.
 * @param {Function} params.setLoading - Loading state setter.
 * @param {Function} params.t - Translation function.
 * @param {Function} params.navigate - Router navigation function.
 * @param {string} params.successUrl - Success redirect URL.
 */
export const registerInBuilding = async ({
    dispatch,
    resolvedUserId,
    buildingId,
    buildingApartmentId,
    setLoading,
    t,
    navigate,
    successUrl
}) => {
    try {
        const {
            payload: {
                data: { success }
            }
        } = await dispatch(PublicProcessCompleted(resolvedUserId));

        if (!success) {
            throw new Error('process_completed_failed');
        }

        handleSuccess({ setLoading, t, navigate, successUrl });
    } catch (error) {
        console.error(error);
        handleFailed({ setLoading, navigate, resolvedUserId, buildingId });
    }
};

/**
 * Saves the Veridas result of the resident and, when it is stored, closes the
 * building registration. It is the building counterpart of
 * registerInEventVeridas, without any of the event endpoints.
 *
 * @param {Object} params
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {string|number} params.resolvedUserId - User identifier.
 * @param {string|number} params.buildingId - Building identifier.
 * @param {string} [params.buildingApartmentId] - Apartment the resident is linked to.
 * @param {Function} params.setLoading - Loading state setter.
 * @param {Function} params.t - Translation function.
 * @param {Function} params.navigate - Router navigation function.
 * @param {string} params.successUrl - Success redirect URL.
 * @param {Object} params.data - Validation response data.
 * @param {string|number} params.validationId - Veridas validation identifier.
 */
export const registerInBuildingVeridas = ({
    dispatch,
    resolvedUserId,
    buildingId,
    buildingApartmentId,
    setLoading,
    t,
    navigate,
    successUrl,
    data,
    validationId
}) => {

    dispatch(
        updateVeridasUser({
            userId: resolvedUserId,
            validationId,
            ocrAndScoresResponse: data,
        })
    ).then(({ payload }) => {
        const responseData = payload?.data?.data;

        if (responseData) {
            registerInBuilding({
                dispatch,
                resolvedUserId,
                buildingId,
                buildingApartmentId,
                setLoading,
                t,
                navigate,
                successUrl
            });
        } else {
            handleFailed({ setLoading, navigate, resolvedUserId, buildingId });
        }
    }).catch(() => {
        handleFailed({ setLoading, navigate, resolvedUserId, buildingId });
    });
};
